import type { RouteLocationNormalizedLoaded, Router } from "vue-router";
import type { OptionsDropdownOption } from "@/components/features/OptionsDropdown";
import { resolveBreadcrumbQuery } from "@/composables/breadcrumb-query";
import { exportAgent } from "@/pages/agents/core/AgentsTable";
// import { openAgentDeploy } from "@/pages/agents/deploy/AgentDeploy";
import { useAgentsQuery } from "@/queries/agents.query";
import { $t } from "@/services/i18n";
import { hasAnyPermission } from "@/stores/user-profile.store";
import { getChargeSubtypeDesign, getChargeSubtypeLabel } from "@/utils/charge-types";

// Utilitário para criar seletor de páginas de agente
export function createAgentPageSelector() {
  return {
    type: "page-selector" as const,
    getOptions: (params: Record<string, any>) => {
      const agentId = params.agent_id as string;

      const options: { value: string; label: string; icon: string; route: Record<string, any> }[] = [
        {
          value: "Agent Playground",
          label: $t("agents:chat_playground.label"),
          icon: "lucide:message-circle",
          route: {
            name: "Agent Playground",
            params: { agent_id: agentId, thread_id: "new" },
          },
        },
      ];

      // Agent Conversations — read-only conversation viewer
      options.push({
        value: "Agent Conversations",
        label: $t("agents:conversations"),
        icon: "lucide:message-square-text",
        route: {
          name: "Agent Conversations",
          params: { agent_id: agentId },
        },
      });

      // Edit Agent — only if user can edit
      if (hasAnyPermission("agents:update", "agents:update:own")) {
        options.push({
          value: "Edit Agent",
          label: $t("menu.edit_agent"),
          icon: "lucide:pencil",
          route: {
            name: "Edit Agent",
            params: { agent_id: agentId, tab: "profile" },
          },
        });
      }

      // Agent Deploy — always visible (view-only for non-editors)
      options.push({
        value: "Agent Deploy",
        label: $t("deployments"),
        icon: "lucide:rocket",
        route: {
          name: "Agent Deploy",
          params: { agent_id: agentId },
        },
      });

      return options;
    },
  };
}

// Utilitário para criar ações de agente
export function createAgentActions() {
  return (payload: { route: RouteLocationNormalizedLoaded; router: Router }) => {
    const agentId = payload.route.params.agent_id as string;
    const agentsQuery = resolveBreadcrumbQuery(useAgentsQuery);
    const agent = agentsQuery.data.value?.find((a) => a.id === agentId);

    if (!agent) return [];

    return [
      {
        label: $t("export"),
        icon: "lucide:upload",
        onSelect: () => exportAgent(agent),
      },
    ] as OptionsDropdownOption[];
  };
}

// Utilitário para criar seletor de entidade (reutilizável)
export function createEntitySelector<T>(
  storeGetter: () => { data: { value: T[] } },
  idKey: keyof T,
  labelKey: keyof T,
  paramKey: string
) {
  return {
    type: "entity-selector" as const,
    getOptions: (params: Record<string, any>) => {
      const store = storeGetter();
      return store.data.value.map((item) => ({
        value: String(item[idKey]),
        label: String(item[labelKey]),
        route: {
          params: {
            ...params,
            [paramKey]: String(item[idKey]),
          },
        },
      }));
    },
  };
}

// Utilitário para criar seletor de páginas do playground
export function createPlaygroundPageSelector() {
  return {
    type: "page-selector" as const,
    getOptions: () => {
      const routes = [
        {
          name: "Text Generation",
          chargeType: "text-generation",
        },
        {
          name: "Speech to Text",
          chargeType: "speech-to-text",
        },
        {
          name: "Text to Speech",
          chargeType: "text-to-speech",
        },
        {
          name: "Anonymization",
          chargeType: "anonymization",
        },
        {
          name: "Decisions",
          chargeType: "decision",
        },
        {
          name: "Audio Enhancement",
          chargeType: "audio-enhancement",
        },
      ].filter((route) => {
        // Aplicar lógica condicional para audio-enhancement
        if (route.chargeType === "audio-enhancement") {
          // return hasFeatureEnabled("audio-enhancement");
          return false; // Temporariamente desativado
        }
        return true;
      });

      return routes.map((route) => ({
        value: route.name,
        label: getChargeSubtypeLabel(route.chargeType),
        icon: getChargeSubtypeDesign(route.chargeType)?.icon,
        route: {
          name: route.name,
        },
      }));
    },
  };
}
